import { CSSProperties } from 'react';
import { SelectOption } from '../types/input';
import { ChildNestedSelectOption, NestedSelectOption, ParentCheckStatus } from '../types';

/**
 * isNullOrUndefined
 * @description - validate if an element is null or undefined
 * @function
 * @param {any} value - Element to validate
 * @return {boolean} Element is null or undefined.
 */
export const isNullOrUndefined = (value: any): boolean => value === undefined || value === null;

/**
 * includeTerm
 * @description - validate if an term text is included into a text
 * @function
 * @param {string} term - some chars to be checked into the text
 * @param {string} text - the text to be checked by the term
 * @return {boolean} Term is included within the text
 */
export const includeTerm = (term: string, text: string) => text.toLowerCase().includes(term.toLocaleLowerCase());

/**
 * hasProp
 * @description - validate if an object has the prop passed arg
 * @function
 * @param {any} obj - Object to validate
 * @param {string} prop - prop's key to check if it belongs to the obj
 * @return {boolean} The obj does has the prop.
 */
export const hasProp = (obj = {}, prop: string): boolean => {
  if (obj === null || typeof obj !== 'object') return false;
  return prop in obj;
};

/**
 * setMaxHeightByOptions
 * @description - Set the max height by a list of options
 * @param {SetMaxHeightByOptions} options - the list length, the max amount of options, and max height
 * @returns {CSSProperties}
 */
interface SetMaxHeightByOptions {
  listLength: number;
  maxOptions: number;
  maxHeight: string;
}
export const setMaxHeightByOptions = (options: SetMaxHeightByOptions): CSSProperties => {
  const { listLength, maxOptions, maxHeight } = options;
  const calcHeight = listLength <= maxOptions ? 'auto' : maxHeight;
  return { height: calcHeight };
};

/**
 * generateYearsBetween
 * @description - Generate a range of Years
 * @param {number} startYear - the year when the range starts
 * @param {number} endYear - the year when the range finishs
 * @returns {Array<{ label: string; value: number }>}
 */
export const generateYearsBetween = (startYear = 1980, endYear?: number): Array<{ label: string; value: number }> => {
  const endDate = endYear || new Date().getFullYear();
  let years: Array<{ label: string; value: number }> = [];

  for (let index = startYear; index <= endDate; index++) {
    years.push({ label: index.toString(), value: index });
  }
  return years;
};

/**
 * findIndexByFirstChar
 * @description - Find the item's position based on the first char
 * @param {number} startYear - the year when the range starts
 * @param {number} endYear - the year when the range finishs
 * @returns {number}
 */
export const findIndexByFirstChar = (list: Array<SelectOption> = [], char: string): number => {
  return list.findIndex((item) => item.label.toLowerCase().charAt(0) === char);
};

/**
 * formatBytes
 * @description - parse bytes to KB, MB, GB, etc
 * @function
 * @param {number} bytes - bytes to parse
 * @param {number} decimals - decimals (0) or binary (2) form
 * @return {string} Returns the bytes parsed.
 */
export const formatBytes = (bytes = 0, decimals = 0): string => {
  if (bytes === 0) return '0 Bytes';
  const kb = 1024;
  const parsedDecimal = decimals <= 0 ? 0 : decimals || 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const index = Math.floor(Math.log(bytes) / Math.log(kb));
  return parseFloat((bytes / Math.pow(kb, index)).toFixed(parsedDecimal)) + ' ' + sizes[index];
};

/**
 * getExtensionFile
 * @description - Get the file's extension
 * @param {string} fileName the file name
 * @returns {string} the extension
 */
export const getExtensionFile = (fileName = ''): string => {
  return fileName.split('.').pop() as string;
};

/**
 * toMb
 * @description - parse a number into the megabyte format
 * @function
 * @param {number} bytes - bytes to parse
 * @return {number} Returns the megabyte representation.
 */
export const toMb = (bytes: number): number => {
  return bytes / 1024 / 1024;
};

/**
 * This function is a wrapper to promisify the native callback on the GET File Request
 * @param {FileSystemFileEntry} item
 * @returns File
 */
const getFileAsync = (item: FileSystemFileEntry) => {
  return new Promise<File>((resolve, reject) => {
    item.file(
      (file) => {
        resolve(file);
      },
      (err) => {
        reject(err);
      },
    );
  });
};

/**
 * This function is a wrapper to promisify the native callback on the Read Entries Request
 * @param {FileSystemDirectoryReader} reader
 * @returns File
 */
const readEntriesAsync = (reader: FileSystemDirectoryReader) =>
  new Promise<FileSystemEntry[]>((resolve, reject) => {
    reader.readEntries(
      (entries) => {
        resolve(entries);
      },
      (err) => {
        reject(err);
      },
    );
  });

/**
 * recursiveTreeFollow
 * @description Recursive Function
 * @param {FileSystemEntry} item Directory or file
 * @param {string} path parent path
 */
const recursiveTreeFollow = async (item: FileSystemEntry, path = ''): Promise<Array<File>> => {
  // check if item is a file or a directory
  if (item.isFile) {
    const file = await getFileAsync(item as FileSystemFileEntry);
    return [file];
  }

  if (item.isDirectory) {
    const files: File[] = [];

    // Get folder contents via native API
    // Read more at:
    // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry/createReader
    const dirReader = (item as FileSystemDirectoryEntry).createReader();

    // readEntries only returns a maximum of 100 entries per call.
    // https://stackoverflow.com/questions/23823548
    let entries = await readEntriesAsync(dirReader);
    while (entries.length !== 0) {
      for (let i = 0; i < entries.length; i++) {
        const recursiveFiles = await recursiveTreeFollow(entries[i], path + item.name + '/');
        files.push(...recursiveFiles);
      }
      entries = await readEntriesAsync(dirReader);
    }
    return files;
  }
  return [];
};

/**
 * dropFiles
 * @description handle the files drag and drop functionality
 * @param {React.DragEvent<HTMLInputElement>} event drag event
 * @param {string} path parent path
 * @return {Promise<Array<File>>} a file list
 */
export const dropFiles = (event: React.DragEvent<HTMLInputElement>): Array<File> => {
  const items = event.dataTransfer.files;
  event.preventDefault();

  const files: File[] = [];

  for (let index = 0; index < items.length; index++) {
    const parsedFiles = Array.from(items).filter((item) => item);

    files.push(parsedFiles[index]);
  }

  return files;
};

/**
 * Helper to validate the status of the parent
 * @param {NestedSelectOption} parent The parent to check
 * @returns {ParentCheckStatus} The status of the parent
 */
export const checkParentStatus = (parent: NestedSelectOption): ParentCheckStatus => {
  const allChecked = parent.options?.every((child: ChildNestedSelectOption) => child.checked);
  if (allChecked) return ParentCheckStatus.everyChild;

  const someChecked = parent.options?.some((child: ChildNestedSelectOption) => child.checked);
  if (someChecked) return ParentCheckStatus.someChild;

  if (parent.checked) return ParentCheckStatus.hasNoChild;

  return ParentCheckStatus.noneChild;
};

/**
 * Helper to change the status of the parent
 * @param parent The parent to change
 * @returns The new parent
 */
export const changeParentStatus = (parent: NestedSelectOption) => {
  const innerOptionStatus = checkParentStatus(parent);
  switch (innerOptionStatus) {
    case ParentCheckStatus.everyChild:
      parent.checked = true;
      parent.indeterminate = false;
      break;
    case ParentCheckStatus.someChild:
      parent.checked = false;
      parent.indeterminate = true;
      break;
    default:
      parent.checked = false;
      parent.indeterminate = false;
      break;
  }
};

/**
 * Helper to change the status of the child
 * @param options Current options list
 * @param childValue Child value to change
 * @param checked Child checked status
 * @returns The new options list
 */
export const changeChildCheck = (options: Array<NestedSelectOption>, childValue: string, checked: boolean) => {
  return options.map((parent) => {
    if (!parent.options) return parent;

    parent.options = parent.options?.map((child) => {
      if (child.value === childValue) {
        // Change inner parent check status
        child.checked = checked;
        // Change parent status
        changeParentStatus(parent);
      }
      return child;
    });

    return parent;
  });
};

/**
 * Helper to change the status of the parent
 * @param options Current options list
 * @param parentValue Parent value to change
 * @param checked Parent checked status
 * @returns The new options list
 */
export const changeParentCheck = (
  options: Array<NestedSelectOption>,
  parentValue: string,
  checked: boolean,
  pickNextColor?: (keyId: string) => string | undefined,
) => {
  return options.map((parent) => {
    if (parent.value === parentValue) {
      parent.checked = checked;
      parent.indeterminate = false;

      if (!parent.options) return parent;
      // Change options' check status
      parent.options = parent.options?.map((child) => {
        // Change inner parent check status
        if (checked && pickNextColor) pickNextColor(child.value);

        child.checked = checked;
        return child;
      });
    }
    return parent;
  });
};

/**
 * Helper to reset the options
 * @param options Current options list
 * @returns An array of options resetted
 */
export const resetOptions = (options: Array<NestedSelectOption>) => {
  return options.map((parent) => {
    parent.checked = false;
    parent.indeterminate = false;

    if (!parent.options) return parent;
    // Change options' check status
    parent.options = parent.options?.map((child) => {
      // Change inner parent check status
      child.checked = false;
      return child;
    });

    return parent;
  });
};

/**
 * Helper to filter options by a specific term
 * @param options The options to filter
 * @param term The term to filter
 * @returns The filtered options
 */
export const filterOptionsByTerm = (options: Array<NestedSelectOption>, term: string): Array<NestedSelectOption> => {
  return options.map((parent) => {
    // has children
    if (parent.options) {
      parent.options = parent.options?.map((child) => {
        // Child matchs with the term: show parent and child
        if (includeTerm(term, child.label)) {
          child.isVisible = true;
          parent.isVisible = true;
          parent.isOpen = term !== '';
        } else child.isVisible = false;

        return child;
      });

      // None child matchs: Ask if parent matchs to show it.
      if (parent.options.every((child) => !child.isVisible)) {
        parent.isVisible = includeTerm(term, parent.label);
        if (parent.isVisible) {
          parent.options = parent.options?.map((child) => {
            child.isVisible = true;
            return child;
          });
          parent.isOpen = false;
        }
      }

      // does not have children
    } else parent.isVisible = includeTerm(term, parent.label);

    return parent;
  });
};

/**
 * Helper to get whether the element is selected
 */
export const getDataStateAttribute = (mutationsList: Array<MutationRecord>, currentElement: HTMLElement) => {
  if (!mutationsList.length) return false;

  for (const mutation of mutationsList) {
    const isAttributeMutation = mutation.type === 'attributes';
    const isDataStateAttribute = mutation.attributeName === 'data-state';
    const isStateMutation = isAttributeMutation && isDataStateAttribute;
    if (isStateMutation) return false;

    const becomeActive = currentElement.getAttribute('data-state') === 'active';
    return becomeActive;
  }
};
